import { Component, inject, signal, ChangeDetectionStrategy, effect, HostListener, ElementRef } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import { CommonModule, DecimalPipe } from '@angular/common';
import { SubscriptionService, SubscriptionPlan, SubscriptionPlanDetail } from '../../services/subscription.service';
import { FeatureFunctionService, FeatureFunction } from '../../services/feature-function.service';
import { CheckoutComponent } from '../checkout/checkout.component';
import { AuthService } from '../../services/auth.service';
import { FooterComponent } from '../footer/footer.component';
import { PricingDetailModalComponent } from '../pricing-detail-modal/pricing-detail-modal.component';
import { FeatureDetailModalComponent } from '../feature-detail-modal/feature-detail-modal.component';

@Component({
  selector: 'app-landing-page',
  imports: [CommonModule, DecimalPipe, CheckoutComponent, FooterComponent, PricingDetailModalComponent, FeatureDetailModalComponent],
  templateUrl: './landing-page.component.html',
  styleUrls: ['./landing-page.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LandingPageComponent {
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private subscriptionService = inject(SubscriptionService);
  private featureFunctionService = inject(FeatureFunctionService);
  protected authService = inject(AuthService);
  private elementRef = inject(ElementRef);

  // Konvertiere queryParams zu Signal für reaktive Verfolgung
  protected queryParams = toSignal(this.route.queryParams);

  plans = signal<SubscriptionPlan[]>([]);
  features = signal<FeatureFunction[]>([]);
  loading = signal(false);
  showCheckout = signal(false);
  selectedPlan = signal<SubscriptionPlan | null>(null);
  showPlanDetailModal = signal(false);
  selectedPlanDetail = signal<SubscriptionPlanDetail | null>(null);
  showFeatureDetailModal = signal(false);
  selectedFeature = signal<FeatureFunction | null>(null);
  showUserMenu = signal(false);

  constructor() {
    // KONSOLIDIERTER Effect - alles an einem Ort, keine Race Conditions
    effect(() => {
      const params = this.queryParams();
      const loadedPlans = this.plans();
      
      if (!params || !loadedPlans.length) return;
      
      // 1. showCheckout Flag setzen wenn query param da ist (sowohl showCheckout als auch openCheckout)
      if (params['showCheckout'] === 'true' || params['openCheckout'] === 'true') {
        this.showCheckout.set(true);
      }
      
      // 2. Plan aus planKey oder plan query param finden
      const planKey = params['planKey'] || params['plan'];
      if (planKey) {
        const plan = loadedPlans.find(p => p.planKey === planKey);
        if (plan) {
          this.selectedPlan.set(plan);
        }
      }
      
      // 3. Wenn kein showCheckout und kein planKey mehr in params, reset
      if (!params['showCheckout'] && !params['openCheckout'] && !params['planKey'] && !params['plan']) {
        this.showCheckout.set(false);
        this.selectedPlan.set(null);
      }
    });
  }

  ngOnInit() {
    this.loadPlans();
    this.loadFeatures();
    
    // Schließe Checkout Modal wenn Zahlung abgeschlossen wurde
    if (localStorage.getItem('paymentCompleted') === 'true') {
      this.showCheckout.set(false);
      this.selectedPlan.set(null);
      localStorage.removeItem('paymentCompleted');
      localStorage.removeItem('selectedPlan');
    }
  }

  loadPlans() {
    this.loading.set(true);
    this.subscriptionService.getAvailablePlans().subscribe({
      next: (plans) => {
        this.plans.set(plans);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Fehler beim Laden der Pläne:', error);
        this.loading.set(false);
      }
    });
  }

  loadFeatures() {
    this.featureFunctionService.getActiveFeatures().subscribe({
      next: (features) => {
        this.features.set(features);
      },
      error: (error) => {
        console.error('Fehler beim Laden der Features:', error);
      }
    });
  }

  navigateToRegister(planKey?: string) {
    if (planKey) {
      this.router.navigate(['/register'], { queryParams: { plan: planKey } });
    } else {
      this.router.navigate(['/register']);
    }
  }

  navigateToLogin() {
    this.router.navigate(['/login']);
  }

  scrollToPricing() {
    const element = document.getElementById('pricing');
    element?.scrollIntoView({ behavior: 'smooth' });
  }

  showPlanDetails(plan: SubscriptionPlan) {
    this.loading.set(true);
    this.subscriptionService.getPlanDetails(plan.planKey).subscribe({
      next: (details) => {
        this.selectedPlanDetail.set(details);
        this.showPlanDetailModal.set(true);
        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading plan details:', error);
        this.loading.set(false);
        // Fallback: direkt zur Auswahl
        this.selectPlan(plan);
      }
    });
  }

  closePlanDetailModal() {
    this.showPlanDetailModal.set(false);
    this.selectedPlanDetail.set(null);
  }

  selectPlanFromModal(planDetail: SubscriptionPlanDetail) {
    // Finde den entsprechenden Plan aus der Liste
    const plan = this.plans().find(p => p.planKey === planDetail.planKey);
    if (plan) {
      this.closePlanDetailModal();
      this.selectPlan(plan);
    }
  }

  selectPlan(plan: SubscriptionPlan) {
    // Wenn Trial, direkt zur Registrierung
    if (plan.planKey === 'trial') {
      this.navigateToRegister(plan.planKey);
    } else {
      // Für bezahlte Pläne: Checkout anzeigen oder erst Login prüfen
      const isAuthenticated = this.authService.isAuthenticated();
      if (isAuthenticated) {
        this.selectedPlan.set(plan);
        this.showCheckout.set(true);
        // Setze Query Parameter um Popup-State und Plan-Selection bei Navigation zu erhalten
        this.router.navigate([], { queryParams: { showCheckout: 'true', planKey: plan.planKey }, queryParamsHandling: 'merge', relativeTo: this.route, replaceUrl: true });
      } else {
        // User zur Registrierung mit Plan in QueryParams
        this.navigateToRegister(plan.planKey);
      }
    }
  }

  closeCheckout() {
    this.showCheckout.set(false);
    this.selectedPlan.set(null);
    // Entferne Query Parameter um saubere URL zu haben
    this.router.navigate([], { queryParams: {}, relativeTo: this.route, replaceUrl: true });
  }

  showFeatureDetails(feature: FeatureFunction) {
    this.selectedFeature.set(feature);
    this.showFeatureDetailModal.set(true);
  }

  closeFeatureDetailModal() {
    this.showFeatureDetailModal.set(false);
    this.selectedFeature.set(null);
  }

  // User Menu Methods
  toggleUserMenu() {
    this.showUserMenu.update(open => !open);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (!this.showUserMenu()) return;
    const target = event.target as HTMLElement;
    const container = this.elementRef.nativeElement.querySelector('.user-menu-container');
    if (container && !container.contains(target)) {
      this.showUserMenu.set(false);
    }
  }

  onCalculation() {
    this.showUserMenu.set(false);
    this.router.navigate(['/app']);
  }

  onSubscriptionManagement() {
    this.showUserMenu.set(false);
    this.router.navigate(['/subscription-management']);
  }

  onChangePassword() {
    this.showUserMenu.set(false);
    this.router.navigate(['/change-password']);
  }

  onLogout() {
    this.showUserMenu.set(false);
    this.authService.logout();
  }
}
